import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, Eye, TrendingUp, Download, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { communicationService } from '../../../services/communicationService';

const DeliveryTracking = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadCampaigns();
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (selectedCampaign) {
      loadExecutions();
    }
  }, [selectedCampaign]);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const result = await communicationService.getAllCampaigns({
        status: 'COMPLETED',
        page: currentPage,
        pageSize: pageSize
      });
      setCampaigns(result.data || []);
      setTotalCount(result.total || 0);
      setTotalPages(Math.ceil((result.total || 0) / pageSize));
    } catch (error) {
      console.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const loadExecutions = async () => {
    if (!selectedCampaign) return;
    
    try {
      // Load all executions for the modal (no pagination needed for modal view)
      const result = await communicationService.getExecutionsByCampaign(selectedCampaign.id, { page: 1, pageSize: 1000 });
      setExecutions(result.data || []);
    } catch (error) {
      console.error('Error loading executions:', error);
    }
  };

  const deliveryStats = {
    totalSent: campaigns.reduce((sum, c) => sum + c.sent_count, 0),
    delivered: campaigns.reduce((sum, c) => sum + c.delivered_count, 0),
    failed: campaigns.reduce((sum, c) => sum + c.failed_count, 0),
    pending: campaigns.reduce((sum, c) => sum + (c.total_recipients - c.sent_count - c.failed_count), 0),
    readRate: campaigns.length > 0 
      ? (campaigns.reduce((sum, c) => sum + c.read_count, 0) / campaigns.reduce((sum, c) => sum + c.delivered_count, 1)) * 100 
      : 0
  };

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs sm:text-sm text-gray-600">Total Sent</span>
            <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">{deliveryStats.totalSent}</p>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs sm:text-sm text-gray-600">Delivered</span>
            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-green-600">{deliveryStats.delivered}</p>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs sm:text-sm text-gray-600">Failed</span>
            <XCircle className="w-3 h-3 sm:w-4 sm:h-4 text-red-600" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-red-600">{deliveryStats.failed}</p>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs sm:text-sm text-gray-600">Pending</span>
            <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-600" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-yellow-600">{deliveryStats.pending}</p>
        </div>

        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs sm:text-sm text-gray-600">Read Rate</span>
            <Eye className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-purple-600">{deliveryStats.readRate.toFixed(1)}%</p>
        </div>
      </div>

      {/* Campaigns Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Campaign Delivery Status</h3>
            <button className="flex items-center gap-2 px-3 sm:px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm">
              <Download className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Export Report</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <p>No completed campaigns found</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Executed At
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Recipients
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Delivered
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Read
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {campaigns.map(campaign => (
                    <tr 
                      key={campaign.id}
                      onClick={() => setSelectedCampaign(campaign)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{campaign.name}</div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-600">
                        {campaign.executed_at ? new Date(campaign.executed_at).toLocaleString() : 'N/A'}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                          {campaign.communication_campaign_type}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                        {campaign.total_recipients}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-xs sm:text-sm font-medium text-green-600">{campaign.delivered_count}</span>
                          {campaign.failed_count > 0 && (
                            <span className="text-xs text-red-600">({campaign.failed_count} failed)</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-xs sm:text-sm font-medium text-purple-600">{campaign.read_count}</span>
                          {campaign.delivered_count > 0 && (
                            <span className="text-xs text-gray-500">
                              ({Math.round((campaign.read_count / campaign.delivered_count) * 100)}%)
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          campaign.status === 'COMPLETED'
                            ? 'bg-green-100 text-green-700' :'bg-yellow-100 text-yellow-700'
                        }`}>
                          {campaign.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden divide-y divide-gray-200">
              {campaigns.map(campaign => (
                <div
                  key={campaign.id}
                  onClick={() => setSelectedCampaign(campaign)}
                  className="p-3 sm:p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-gray-900 text-sm sm:text-base">{campaign.name}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded flex-shrink-0 ${
                      campaign.status === 'COMPLETED'
                        ? 'bg-green-100 text-green-700' :'bg-yellow-100 text-yellow-700'
                    }`}>
                      {campaign.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm mb-2">
                    <div>
                      <span className="text-gray-600">Type:</span>
                      <p className="font-medium text-gray-900">{campaign.communication_campaign_type}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Recipients:</span>
                      <p className="font-medium text-gray-900">{campaign.total_recipients}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Delivered:</span>
                      <p className="font-medium text-green-600">{campaign.delivered_count}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Read:</span>
                      <p className="font-medium text-purple-600">{campaign.read_count}</p>
                    </div>
                  </div>
                  {campaign.executed_at && (
                    <p className="text-xs text-gray-500">
                      Executed: {new Date(campaign.executed_at).toLocaleString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Show:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-2 py-1 border border-gray-300 rounded text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs sm:text-sm text-gray-600">per page</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-gray-600">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs sm:text-sm text-gray-700 px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">{selectedCampaign.name}</h3>
                <button
                  onClick={() => setSelectedCampaign(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Executed At:</span>
                  <p className="font-medium text-gray-900 text-sm sm:text-base">
                    {selectedCampaign.executed_at ? new Date(selectedCampaign.executed_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Campaign Type:</span>
                  <p className="font-medium text-gray-900 text-sm sm:text-base">{selectedCampaign.communication_campaign_type}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Total Recipients:</span>
                  <p className="font-medium text-gray-900 text-sm sm:text-base">{selectedCampaign.total_recipients}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Sent:</span>
                  <p className="font-medium text-gray-900 text-sm sm:text-base">{selectedCampaign.sent_count}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Delivered:</span>
                  <p className="font-medium text-green-600 text-sm sm:text-base">{selectedCampaign.delivered_count}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Failed:</span>
                  <p className="font-medium text-red-600 text-sm sm:text-base">{selectedCampaign.failed_count}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Read:</span>
                  <p className="font-medium text-purple-600 text-sm sm:text-base">{selectedCampaign.read_count}</p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600">Status:</span>
                  <p className="font-medium text-gray-900 text-sm sm:text-base">{selectedCampaign.status}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryTracking;
